import { Injectable } from '@angular/core';
import { HttpEvent } from '@angular/common/http';

import { Store } from '@ngrx/store';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { combineLatest } from 'rxjs';
import { withLatestFrom, switchMap, map, tap, take } from 'rxjs/operators';
import { Update } from '@ngrx/entity';

import { IAuroraState } from '@apps/aurora.state';
import * as fromAuthStore from '@apps/auth/store';
import { Profile } from '@apps/auth/models';
import { getAllTags } from '@apps/cmdb/store';
import { EnrichedAsset, Tag } from '@apps/cmdb/models';
import { TypeAssetName } from '@apps/cmdb/cmdb.constants';
import { joinAssets } from '@common/operators/join-assets';
import { AssetMiscDomainService } from '@apps/cmdb/domain/asset-misc-domain.service';
import { Assetify } from '@common/domain/assetify';

import * as fromBckpCapacityActions from './capacities.actions';
import
{
    getAllCapacitiesByIdentifier,
    getBckpCapacitiesArraySinceUntil, getBckpCapacitiesExportQueryString
} from '../bckp.reducer';
import { ChargedCapacityListDomainService } from '../../domain/charged-capacity-list-domain.service';
import { CapacityGbytesPerServer } from '../../domain/models';
import { NotificationService, FilesService, NotfGravity, NotifType } from '@common/services';
import { ChargedCapacityMiscDomainService } from '../../domain/charged-capacity-misc-domain.service';

@Injectable()
export class BckpCapacitiesEffects
{
    constructor (
        private actions: Actions,
        private store: Store<IAuroraState>,
        private chargedCapacityListDomainService: ChargedCapacityListDomainService,
        private chargedCapacityMiscDomainService: ChargedCapacityMiscDomainService,
        private notificationService: NotificationService,
        private filesService: FilesService,
        private assetMiscDomainService: AssetMiscDomainService
    )
    {
    }

    bckpCapacityODataRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpCapacityActions.BckpCapacitiesODataRequested),
                    withLatestFrom(
                        this.store.select(getBckpCapacitiesArraySinceUntil),
                        this.store.select(getAllTags),
                        (_, qs, tags: Tag[]) => ({
                            qs,
                            tags
                        })
                    ),
                    switchMap(({ qs, tags }: { qs: string[], tags: Tag[]; }) =>
                        this.chargedCapacityListDomainService.getChargedCapacities(qs[0], qs[1])
                            .pipe(
                                joinAssets(this.assetMiscDomainService, TypeAssetName.HOST, tags),
                                map((cgps: CapacityGbytesPerServer[]) =>
                                    fromBckpCapacityActions.BckpCapacitiesCgpsLoaded({ cgps })
                                )
                            )
                    )
                )
    );

    bckpCapacitiesFilterApplied = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpCapacityActions.BckpCapacitiesFilterApplied),
                    tap(() =>
                    {
                        this.store.dispatch(fromBckpCapacityActions.BckpCapacitiesCleared());
                        this.store.dispatch(fromBckpCapacityActions.BckpCapacitiesODataRequested());
                    })
                ),
        { dispatch: false }
    );

    bckpCapacitiesExportRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpCapacityActions.BckpCapacitiesExportRequested),
                    withLatestFrom(this.store.select(getBckpCapacitiesExportQueryString)),
                    switchMap(([params, qs]: [any, string]) =>
                        this.chargedCapacityMiscDomainService.export(qs, params.format)
                            .pipe(
                                map((response: HttpEvent<Blob>) =>
                                    fromBckpCapacityActions.BckpCapacitiesExportSucceeded({ response })
                                )
                            )
                    )
                )
    );

    bckpCapacitiesExportSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpCapacityActions.BckpCapacitiesExportSucceeded),
                    withLatestFrom(this.store.select(fromAuthStore.getProfile)),
                    tap(([params, profile]: [{ response: HttpEvent<Blob>; }, Profile]) =>
                    {
                        this.filesService.saveFileFromApi(params.response, profile);
                    })
                ),
        { dispatch: false }
    );

    bckpCapacitiesTagAttachRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpCapacityActions.BckpCapacitiesTagAttachRequested),
                    switchMap((params) =>
                        this.assetMiscDomainService.attachTag(TypeAssetName.HOST, params.identifier, params.name, params.tagId)
                            .pipe(
                                map((enrichedAsset: EnrichedAsset) =>
                                    fromBckpCapacityActions.BckpCapacitiesTagAttachExecuted({ enrichedAsset })
                                )
                            )
                    )
                )
    );

    bckpCapacitiesTagAttachExecuted = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpCapacityActions.BckpCapacitiesTagAttachExecuted),
                    switchMap((params) =>
                        combineLatest([
                            this.store.select(getAllCapacitiesByIdentifier(params.enrichedAsset.identifier)),
                            this.store.select(getAllTags)
                        ])
                            .pipe(
                                take(1),
                                tap(([capacities, tags]: [CapacityGbytesPerServer[], Tag[]]) =>
                                {
                                    capacities.forEach((capacity: CapacityGbytesPerServer) =>
                                    {
                                        const updatedCapacity: Update<CapacityGbytesPerServer> = {
                                            id: capacity.serverShort,
                                            changes: {
                                                tagIds: params.enrichedAsset.tagIds,
                                                tagFullNames: Assetify.BuildFullTagNames(params.enrichedAsset.tagIds, tags)
                                            }
                                        };
                                        this.store.dispatch(fromBckpCapacityActions.BckpCapacitiesTagAttachSucceeded({ updatedCapacity: updatedCapacity }));
                                    });
                                })
                            )
                    )
                ),
        { dispatch: false }
    );

    bckpCapacitiesTagAttachSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpCapacityActions.BckpCapacitiesTagAttachSucceeded),
                    tap(() =>
                        this.notificationService.notify(
                            [
                                'CMDB.ASSETS.ATTACH.TITLE',
                                'CMDB.ASSETS.ATTACH.SUCCEEDED'
                            ],
                            NotfGravity.success,
                            NotifType.SNACKBAR
                        )
                    )
                ),
        { dispatch: false }
    );

    bckpCapacitiesTagDetachRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpCapacityActions.BckpCapacitiesTagDetachRequested),
                    switchMap((params) =>
                        this.assetMiscDomainService.detachTag(TypeAssetName.HOST, params.identifier, params.tagId)
                            .pipe(
                                map((enrichedAsset: EnrichedAsset) =>
                                    fromBckpCapacityActions.BckpCapacitiesTagDetachExecuted({ enrichedAsset })
                                )
                            )
                    )
                )
    );

    bckpCapacitiesTagDetachExecuted = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpCapacityActions.BckpCapacitiesTagDetachExecuted),
                    switchMap((params) =>
                        combineLatest([
                            this.store.select(getAllCapacitiesByIdentifier(params.enrichedAsset.identifier)),
                            this.store.select(getAllTags)
                        ])
                            .pipe(
                                take(1),
                                tap(([capacities, tags]: [CapacityGbytesPerServer[], Tag[]]) =>
                                {
                                    capacities.forEach((capacity: CapacityGbytesPerServer) =>
                                    {
                                        const updatedCapacity: Update<CapacityGbytesPerServer> = {
                                            id: capacity.serverShort,
                                            changes: {
                                                tagIds: params.enrichedAsset.tagIds,
                                                tagFullNames: Assetify.BuildFullTagNames(params.enrichedAsset.tagIds, tags)
                                            }
                                        };
                                        this.store.dispatch(fromBckpCapacityActions.BckpCapacitiesTagDetachSucceeded({ updatedCapacity: updatedCapacity }));
                                    });
                                })
                            )
                    )
                ),
        { dispatch: false }
    );

    bckpCapacitiesTagDetachSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpCapacityActions.BckpCapacitiesTagDetachSucceeded),
                    tap(() =>
                        this.notificationService.notify(
                            [
                                'CMDB.ASSETS.DETACH.TITLE',
                                'CMDB.ASSETS.DETACH.SUCCEEDED'
                            ],
                            NotfGravity.success,
                            NotifType.SNACKBAR
                        )
                    )
                ),
        { dispatch: false }
    );
}
